import React, { useState, useEffect } from 'react';
import MapCountries from '../common/map/map-countries/map-countries';
import globalResilienceData from '../../data/globalResilienceData.json';
import IconGlobal from '../common/icon/icon-global';
import IconResilience from '../common/icon/icon-resilience';
import IconRisk from '../common/icon/icon-risk';
import IconClose from '../common/icon/icon-close';
import IconArrowSelect from '../common/icon/icon-arrow-select';
import styles from './GlobalResilienceMap.module.scss';

// Generate countries data from resilience dataset
const generateCountriesFromResilienceData = (metric = 'totalScore') => {
  const countriesMap = {};

  globalResilienceData.forEach(country => {
    if (country.iso2 && country.iso2.length >= 2) {
      const value = country[metric] || 0;
      let type, note;

      // Always include global rank and all metric data in tooltip
      note = {
        rank: country.globalRank,
        totalScore: country.totalScore.toFixed(2),
        scoreCategory: country.scoreCategory,
        riskScore: country.riskScore.toFixed(1),
        riskCategory: country.riskCategory,
        resilienceScore: country.resilienceScore.toFixed(2),
        resilienceCategory: country.resilienceCategory
      };

      // Categorize based on the metric with metric-specific color prefixes
      switch (metric) {
        case 'totalScore':
          // For total score: higher = better (darkest blue for highest ranking)
          if (value >= 70) type = 'total_excellent';
          else if (value >= 60) type = 'total_good';
          else if (value >= 50) type = 'total_fair';
          else if (value >= 40) type = 'total_poor';
          else type = 'total_critical';
          break;
        case 'riskScore':
          // For risk: use category from data (Very Low, Low, Medium, High, Very High)
          // Map to colors: Very Low = lightest, Very High = darkest
          switch(country.riskCategory) {
            case 'Very Low': type = 'risk_excellent'; break;  // Lightest
            case 'Low': type = 'risk_good'; break;
            case 'Medium': type = 'risk_fair'; break;
            case 'High': type = 'risk_poor'; break;
            case 'Very High': type = 'risk_critical'; break;  // Darkest
            default: type = 'risk_fair';
          }
          break;
        case 'resilienceScore':
          // For resilience: use category from data (Very Low, Low, Medium, High, Very High)
          // Map to colors: Very High = darkest (best), Very Low = lightest (worst)
          switch(country.resilienceCategory) {
            case 'Very High': type = 'resilience_excellent'; break;  // Darkest (best)
            case 'High': type = 'resilience_good'; break;
            case 'Medium': type = 'resilience_fair'; break;
            case 'Low': type = 'resilience_poor'; break;
            case 'Very Low': type = 'resilience_critical'; break;  // Lightest (worst)
            default: type = 'resilience_fair';
          }
          break;
        default:
          type = 'total_fair';
      }

      countriesMap[country.iso2] = {
        code: country.iso2,
        name: country.country,
        type: type,
        note: note,
        region: country.region,
        value: value
      };
    }
  });

  return countriesMap;
};

const GlobalResilienceMap = ({ type, city }) => {
  const selectedCode = null;
  const hoveredCountry = null;
  const [countriesData, setCountriesData] = useState({});
  const [currentMetric, setCurrentMetric] = useState('totalScore');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const metrics = [
    { value: 'totalScore', label: 'Total Global Investment Risk and Resilience Index Score', className: 'totalButton', icon: IconGlobal },
    { value: 'riskScore', label: 'Total Risk Score', className: 'riskButton', icon: IconRisk },
    { value: 'resilienceScore', label: 'Total Resilience Score', className: 'resilienceButton', icon: IconResilience }
  ];

  useEffect(() => {
    const countries = generateCountriesFromResilienceData(currentMetric);
    setCountriesData(countries);
  }, [currentMetric]);

  const handleMetricChange = (metric) => {
    setCurrentMetric(metric);
    setDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const currentMetricInfo = metrics.find(m => m.value === currentMetric);

  // Get legend title based on current metric
  const getLegendTitle = () => {
    switch (currentMetric) {
      case 'totalScore':
        return {
          main: 'Total Global Investment Risk and Resilience Index Score',
          sub: '(Out of 100, Higher is Better)'
        };
      case 'riskScore':
        return {
          main: 'Total Risk Score',
          sub: '(Out of 100, Lower is Better)'
        };
      case 'resilienceScore':
        return {
          main: 'Total Resilience Score',
          sub: '(Out of 100, Higher is Better)'
        };
      default:
        return {
          main: 'Score',
          sub: '(Out of 100)'
        };
    }
  };

  return (
    <div className={styles.globalResilienceContainer}>
      <div className="row">
        <div className="col-md-3">
          <div className={styles.metricSelector}>
            {/* Desktop: Show buttons */}
            <div className={styles.metricButtons}>
                <div className={styles.filterLabelTop}>Select Filter</div>
              {metrics.map(metric => {
                const Icon = metric.icon;
                return (
                  <button
                    key={metric.value}
                    className={`${styles.metricButton} ${styles[metric.className]} ${currentMetric === metric.value ? styles.active : ''}`}
                    onClick={() => handleMetricChange(metric.value)}
                  >
                    <Icon />
                    <span>{metric.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Mobile: Show dropdown */}
            <div className={styles.metricDropdown}>
              <div className={styles.filterLabelTop}>Select Filter</div>
              {!dropdownOpen && (
                <button
                  className={`${styles.dropdownToggle} ${currentMetricInfo ? styles[currentMetricInfo.className] : ''}`}
                  onClick={toggleDropdown}
                >
                  <div className={styles.dropdownSelected}>
                    {currentMetricInfo && (
                      <>
                        <currentMetricInfo.icon />
                        <span>{currentMetricInfo.label}</span>
                      </>
                    )}
                  </div>
                  <IconArrowSelect />
                </button>
              )}
              {dropdownOpen && (
                <div className={styles.dropdownPanel}>
                  <div className={styles.dropdownHeader}>
                    <span>Select filter</span>
                    <button className={styles.closeButton} onClick={toggleDropdown}>
                      <IconClose />
                    </button>
                  </div>
                  <div className={styles.dropdownOptions}>
                    {metrics.map((metric) => {
                      const Icon = metric.icon;
                      return (
                        <button
                          key={metric.value}
                          onClick={() => handleMetricChange(metric.value)}
                          className={`${styles.dropdownOption} ${styles[metric.className]} ${currentMetric === metric.value ? styles.active : ''}`}
                        >
                          <Icon />
                          <span>{metric.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-9">
          <div className={styles.mapSection}>
            <div className={`${styles.mapContainer} ${Object.keys(countriesData).length > 0 ? styles.loaded : styles.loading}`}>
              <MapCountries
                listOfCountries={countriesData}
                selectedCode={selectedCode}
                externalItemHovered={hoveredCountry}
                currentMetric={currentMetric}
              />
            </div>
          </div>

          <div className={styles.legendSection}>
            <div className={styles.legendTitle}>
              {getLegendTitle().main}
              <br />
              <span className={styles.legendSubtitle}>{getLegendTitle().sub}</span>
            </div>
            <div className={styles.legend}>
              <span className={styles.legendLabel}>0</span>
              <div className={`${styles.gradientBar} ${styles[currentMetric]}`}></div>
              <span className={styles.legendLabel}>100</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalResilienceMap;